import React from 'react';
import styled, { css } from 'styled-components';
import { Caption, Overline } from '../..';

export const Input = styled.input<{ hasError?: boolean; success?: boolean }>`
    font-family: ${({ theme }) => theme.bento.font.primary.family};
    box-sizing: border-box;
    background: ${({ theme }) => theme.bento.colors.background.main};
    border: 1px solid ${({ theme }) => theme.bento.colors.border.main};
    border-radius: 4px;
    padding: 5px 8px;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.bento.colors.text.main};
    outline: none;
    width: 100%;

    ${({ hasError, theme }) =>
        hasError &&
        css`
            border: 1px solid ${theme.bento.colors.danger.main};
        `}

    ${({ disabled }) =>
        !disabled &&
        css`
            &:hover {
                border: 1px solid ${({ theme }) => theme.bento.colors.border.strong};
            }
        `}

    ${({ hasError, success, theme }) =>
        success &&
        !hasError &&
        css`
            border: 1px solid ${theme.bento.colors.success.main};
        `}

    &::placeholder {
        color: ${({ theme }) => theme.bento.colors.text.weak};
    }

    &:focus {
        border: 1px solid ${({ theme }) => theme.bento.colors.primary.main};
        box-shadow: 0 0 0 2px ${({ theme }) => theme.bento.colors.primary.weak};
    }
    &:disabled {
        color: ${({ theme }) => theme.bento.colors.neutral.main};
        opacity: 0.4;
    }
`;

const Label = styled(Overline)<{ htmlFor?: string }>`
    width: 100%;
    padding-bottom: 8px;
`;

const StyledCaption = styled(Caption)`
    text-align: right;
    width: 100%;
    padding-top: 8px;
`;

const Error = styled(StyledCaption)`
    color: ${({ theme }) => theme.bento.colors.danger.main};
`;

const StyledInputGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
`;

const InputWrap = styled.div`
    position: relative;
    width: 100%;
`;

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
    {
        [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
    }[Keys];

export interface InputGroupProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: React.ReactChild;
    caption?: React.ReactChild;
    error?: React.ReactChild;
    success?: boolean;
}

export type AccessibleInputGroupProps = RequireAtLeastOne<InputGroupProps, 'label' | 'placeholder'>;

export const InputGroup = React.forwardRef<HTMLInputElement, AccessibleInputGroupProps>(
    ({ label, caption, error, success, className, id, children, ...rest }, ref) => {
        return (
            <StyledInputGroup className={className}>
                {label && (
                    <Label as={'label'} htmlFor={id}>
                        {label}
                    </Label>
                )}
                <InputWrap>
                    <Input id={id} hasError={!!error} success={success} ref={ref} {...rest} />
                    {children}
                </InputWrap>
                {caption && <Caption>{caption}</Caption>}
                {error && <Error>{error}</Error>}
            </StyledInputGroup>
        );
    },
);
